import React, { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import moment from 'moment';
import {
    NumberInput, DatePicker, Select, Title,
    Icon,
} from 'ui-library';
import { compareDates } from 'utils/datetime';
import { useTranslation } from 'react-i18next';
import { ICON_EDIT_OUTLINE, ICON_STOP } from 'ui-library/components/Icon';
import { ACTIVE_SWITCHER_ADJUST, ACTIVE_SWITCHER_STOP } from 'datasource/ChangeWithdrawal/constants';
import './ChangeWithdrawalPlanSummary.css';
import { DEFAULT_APP_CURRENCY } from 'constants/constants';

const ChangeWithdrawalPlanSummary = ({ datasource }: {datasource: any}) => {
    const {
        data,
        setData,
        control,
        trigger,
        errorData,
        withdrawalPeriodicityOptions,
        hasWithdrawalData,
        activeSwitcher,
        handleAdjustWithdrawal,
        handleStopWithdrawal,
        withdrawalDataFromAPI,
    } = datasource({});

    const { t } = useTranslation();

    const isFormDisabled = activeSwitcher === ACTIVE_SWITCHER_STOP;

    const handleFrequencyOnChange = useCallback((val) => {
        if (val !== withdrawalDataFromAPI?.frequency) {
            setData({
                ...data, frequency: val, startDate: moment(withdrawalDataFromAPI?.startOfWithdrawal)?.format('MM/YYYY'), withdrawalsStartDate: moment(withdrawalDataFromAPI?.startOfWithdrawal)?.format('MM/YYYY'),
            });
        } else {
            setData({
                ...data, frequency: val, startDate: null, withdrawalsStartDate: null,
            });
        }
    }, [withdrawalDataFromAPI?.startOfWithdrawal, data]);

    return (
        <div>
            {hasWithdrawalData
                ? (
                    <div>
                        <Title type={1} className="font-normal">
                            {t('3b.withdrawalPlan.ChangeWithdrawalSummary.Title.Manage')}
                        </Title>
                        <div className="manage-withdrawal-switcher">
                            <button type="button" className={activeSwitcher === ACTIVE_SWITCHER_ADJUST ? 'active-switcher' : ''} onClick={handleAdjustWithdrawal}>
                                <Icon size={20} type={ICON_EDIT_OUTLINE} />
                                <span>{t('3b.withdrawalPlan.ChangeWithdrawalSummary.Title.adjust')}</span>
                            </button>
                            <button type="button" className={isFormDisabled ? 'active-switcher' : ''} onClick={handleStopWithdrawal}>
                                <Icon size={18} type={ICON_STOP} />
                                <span>{t('3b.withdrawalPlan.ChangeWithdrawalSummary.Title.stop')}</span>
                            </button>
                        </div>
                    </div>
                )
                : (
                    <Title type={1} className="font-normal">
                        {t('3b.withdrawalPlan.ChangeWithdrawalSummary.Title.Configure')}
                    </Title>
                )}

            <div className="form">
                <Controller
                    name="amount"
                    control={control}
                    render={({ field: { onChange, ...field } }) => (
                        <NumberInput
                            {...field}
                            placeholder={t('3b.withdrawalPlan.investmentAmountPlaceholder')}
                            thousandSeparator="’"
                            decimalSeparator="."
                            prefix={`${DEFAULT_APP_CURRENCY} `}
                            label={t('3b.withdrawalPlan.amount')}
                            value={isFormDisabled ? '0.00' : data.amount}
                            onChange={(value: number) => {
                                onChange(value);
                                trigger('amount');
                            }}
                            error={errorData?.amount?.message}
                            required
                            disabled={isFormDisabled}
                            allowNegativeValue={false}
                            textRight
                        />
                    )}
                />

                <Controller
                    name="frequency"
                    control={control}
                    render={({ field: { onChange, ...field } }) => (
                        <Select
                            {...field}
                            placeholder={t('3b.kyc.pleaseSelect')}
                            label={t('3b.withdrawalPlan.frequency')}
                            value={+data?.frequency || null}
                            options={withdrawalPeriodicityOptions || []}
                            error={errorData?.frequency?.message}
                            required
                            disabled={isFormDisabled}
                            onChange={hasWithdrawalData ? handleFrequencyOnChange : onChange}
                        />
                    )}
                />

                <Controller
                    name="startDate"
                    control={control}
                    render={({ field: { onChange, ...field } }) => (
                        <DatePicker
                            {...field}
                            label={t('3b.withdrawalPlan.withdrawalsStart')}
                            value={data?.startDate !== moment(withdrawalDataFromAPI?.startOfWithdrawal)?.format('MM/YYYY') ? data?.startDate : null}
                            defaultValue={null}
                            placeholder="MM/YYYY"
                            disabledDate={(current) => current && (compareDates(new Date(), current))}
                            onChange={(value) => {
                                const date = moment(value).format('MM/YYYY');

                                onChange(date);
                            }}
                            error={errorData?.startDate?.message}
                            format="MM/YYYY"
                            picker="month"
                            required={!(isFormDisabled || (hasWithdrawalData && data?.frequency && withdrawalDataFromAPI?.frequency !== +data?.frequency))}
                            disabled={isFormDisabled || (hasWithdrawalData && data?.frequency && withdrawalDataFromAPI?.frequency !== +data?.frequency)}
                        />
                    )}
                />
            </div>
        </div>

    );
};

export default ChangeWithdrawalPlanSummary;
